import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const About = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef();

  const toggleMode = () => setIsRegistering(!isRegistering);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const recaptchaToken = await window.grecaptcha.execute(
        "6LcdvL4rAAAAABVgoqJkxFf5481zBaYtNBozPLFt",
        { action: "login" }
      );

      const response = await fetch(
        "https://admin-backend-fbuuh6fo3q-el.a.run.app/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password, recaptchaToken }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        login(data); 

        showToast("Logged in successfully!");
        navigate("/dashboard");
      } else {
        showToast(data.error || "Login failed", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const recaptchaToken = await window.grecaptcha.execute(
        "6LcdvL4rAAAAABVgoqJkxFf5481zBaYtNBozPLFt",
        { action: "register" }
      );

      const response = await fetch(
        "https://admin-backend-fbuuh6fo3q-el.a.run.app/auth/register",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username,
            password,
            full_name: fullName,
            email,
            recaptchaToken,
          }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        showToast("Admin registered successfully! Please login.");
        setIsRegistering(false);
        setUsername("");
        setPassword("");
        setFullName("");
        setEmail("");
      } else {
        showToast(data.error || "Registration failed", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-r from-gray-100 to-gray-200">
      {toast.message && (
        <div
          className={`fixed top-5 right-5 px-3 py-2 rounded shadow-lg text-white z-50 text-sm sm:text-base transition ${
            toast.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="md:w-1/2 flex flex-col justify-center items-start p-6 sm:p-10 space-y-4 sm:space-y-6">
        <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 leading-snug">
          Welcome to BIG-Rewardz Admin Portal
        </h1>
        <p className="text-gray-700 text-base sm:text-lg">
          Manage credit card offers, partner organizations, vendor partners, and
          store deals seamlessly. Access comprehensive data and insights to
          provide users with the best offers from multiple banks and vendors.
        </p>
        <ul className="list-disc pl-5 text-gray-700 space-y-1 sm:space-y-2 text-sm sm:text-base">
          <li>View and manage offers from different banks</li>
          <li>Organize partner vendors and stores efficiently</li>
          <li>Analyze offers by product type and category</li>
          <li>Secure login for admin access</li>
        </ul>
      </div>

      <div className="md:w-1/2 flex justify-center items-center p-6 sm:p-10">
        <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-6 sm:p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
            {isRegistering ? "Register Admin" : "Admin Login"}
          </h2>
          <form
            ref={formRef}
            onSubmit={isRegistering ? handleRegister : handleLogin}
            className="flex flex-col gap-3 sm:gap-4"
          >
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm sm:text-base"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm sm:text-base"
              required
            />
            {isRegistering && (
              <>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm sm:text-base"
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm sm:text-base"
                  required
                />
              </>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="bg-gray-900 text-white py-2 rounded-lg font-semibold hover:bg-gray-800 transition flex justify-center items-center text-sm sm:text-base"
            >
              {isLoading ? (
                <span className="loader w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : isRegistering ? (
                "Register"
              ) : (
                "Login"
              )}
            </button>
          </form>
          <div className="text-center mt-4">
            <button
              onClick={toggleMode}
              className="text-blue-600 hover:underline text-xs sm:text-sm"
            >
              {isRegistering
                ? "Already have an account? Login"
                : "New admin? Register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
